import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { authService } from './authService';

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Auth event handler type
type AuthErrorHandler = () => void;

// Retry notification handler type
export interface RetryInfo {
  attempt: number;
  maxAttempts: number;
  delay: number;
  error: Error;
}

type RetryHandler = (info: RetryInfo) => void;

// API Response Types
export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: any;
}

// API Error Class
export class ApiServiceError extends Error {
  constructor(
    public code: string,
    public status: number,
    public details?: any,
    message: string = 'API request failed'
  ) {
    super(message);
    this.name = 'ApiServiceError';
  }
}

// Add types for bounce/complaint API params
export interface GetBouncesParams {
  clientId: number;
  startDate?: string;
  endDate?: string;
  skip?: number;
  limit?: number;
  email_address?: string;
  bounce_type?: string;
  bounce_sub_type?: string;
  common_subject?: string;
  sortBy?: string;
  sortOrder?: string;
}
export interface GetBouncesByDayParams {
  clientId: number;
  days?: number;
}
export interface GetComplaintsParams {
  clientId: number;
  startDate?: string;
  endDate?: string;
  skip?: number;
  limit?: number;
  email_address?: string;
  complaint_feedback_type?: string;
  complaint_sub_type?: string;
  common_subject?: string;
  sortBy?: string;
  sortOrder?: string;
}
export interface GetComplaintsByDayParams {
  clientId: number;
  days?: number;
}

// Add types for unsubscribes API params
export interface GetUnsubscribesParams {
  clientId: number;
  startDate?: string;
  endDate?: string;
  skip?: number;
  limit?: number;
  email_address?: string;
  first_name?: string;
  last_name?: string;
  dnc_email_note?: string;
  sortBy?: string;
  sortOrder?: string;
}
export interface GetUnsubscribesByDayParams {
  clientId: number;
  days?: number;
}

// API Service Class
class ApiService {
  private api: AxiosInstance;
  private static instance: ApiService;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second
  private authErrorHandlers: AuthErrorHandler[] = [];
  private retryHandlers: RetryHandler[] = [];
  private loggedRequests: Set<string> | null = null;

  private constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      timeout: 60000 // 60 second timeout
    });

    // Authentication is via httpOnly cookies — browser handles attachment.
    // We only need to attach the CSRF header on mutating requests.
    this.api.interceptors.request.use(
      (config) => {
        const method = (config.method || 'get').toLowerCase();
        if (method !== 'get' && method !== 'head' && method !== 'options') {
          const csrf = readCookie('mp_csrf');
          if (csrf) {
            config.headers = config.headers || {};
            (config.headers as any)['X-CSRF-Token'] = csrf;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: on 401, attempt one silent refresh, then notify
    // handlers (which typically route to /login).
    let isRefreshing = false;
    let refreshPromise: Promise<any> | null = null;
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        if (
          error.response?.status === 401 &&
          !originalRequest._retried &&
          !originalRequest.url?.includes('/api/auth/refresh') &&
          !originalRequest.url?.includes('/api/auth/login')
        ) {
          originalRequest._retried = true;
          try {
            if (!isRefreshing) {
              isRefreshing = true;
              refreshPromise = authService.refresh().finally(() => {
                isRefreshing = false;
              });
            }
            await refreshPromise;
            return this.api.request(originalRequest);
          } catch (refreshError) {
            this.notifyAuthError();
            return Promise.reject(error);
          }
        }
        if (error.response?.status === 401) {
          this.notifyAuthError();
        }
        return Promise.reject(error);
      }
    );

    // Initialize the Set to track logged requests
    this.loggedRequests = new Set();
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  public clearAuth() {
    // Cookie-based auth: clear by hitting logout which deletes cookies server-side.
    authService.logout();
    this.loggedRequests?.clear();
  }

  // Register a handler for authentication errors (401)
  public registerAuthErrorHandler(handler: AuthErrorHandler): () => void {
    this.authErrorHandlers.push(handler);
    
    // Return a function to unregister the handler
    return () => {
      this.authErrorHandlers = this.authErrorHandlers.filter(h => h !== handler);
    };
  }

  // Notify all auth error handlers
  private notifyAuthError(): void {
    console.warn('ApiService: Authentication error detected, notifying handlers');
    this.authErrorHandlers.forEach(handler => {
      try {
        handler();
      } catch (error) {
        console.error('Error in auth error handler:', error);
      }
    });
  }

  private handleError(error: AxiosError): ApiServiceError {
    if (error.response) {
      // Server responded with error
      const status = error.response.status;
      const data = error.response.data as any;
      
      // Log all server errors
      console.error(`[ApiService] Server Error (${status}):`, {
        endpoint: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status,
        statusText: error.response.statusText,
        errorMessage: data.message || data.detail || error.message,
        errorDetails: data.details || data,
        requestData: error.config?.data,
        timestamp: new Date().toISOString()
      });

      // Enhanced logging for auth/authorization errors
      if (status === 401 || status === 403) {
        console.error(`[ApiService] ${status} Error Details:`, {
          endpoint: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          status,
          statusText: error.response.statusText,
          errorMessage: data.message || error.message,
          errorDetails: data.details,
          selectedClientId: localStorage.getItem('selectedClientId'),
          timestamp: new Date().toISOString()
        });

        // Log specific validation failures
        if (data.validation_error) {
          console.error('[ApiService] Validation Errors:', data.validation_error);
        }

        // Log specific auth failures
        if (status === 401) {
          console.error('[ApiService] Authentication Error:', {
            reason: this.getAuthErrorReason(error),
            tokenExpiry: 'unknown' // Token expiry handled by authService
          });
        }

        // Log specific authorization failures
        if (status === 403) {
          console.error('[ApiService] Authorization Error:', {
            reason: this.getAuthorizationErrorReason(error),
            userRoles: [], // User roles handled by backend
            requiredPermissions: data.required_permissions
          });
        }
      }
      
      return new ApiServiceError(
        data.code || 'UNKNOWN_ERROR',
        status,
        data.details,
        data.message || error.message
      );
    } else if (error.request) {
      // Request made but no response
      if (error.message.includes('Network Error') || error.message.includes('CORS')) {
        console.error('CORS or Network Error:', error.message);
        console.error('This may be caused by missing CORS headers on the server or network connectivity issues.');
        return new ApiServiceError(
          'CORS_ERROR',
          0,
          { originalError: error },
          'CORS policy error or Network issue. Check server CORS configuration.'
        );
      }
      return new ApiServiceError(
        'NETWORK_ERROR',
        0,
        undefined,
        'Network error occurred'
      );
    } else {
      // Request setup error
      return new ApiServiceError(
        'REQUEST_ERROR',
        0,
        undefined,
        error.message
      );
    }
  }

  // Helper to determine specific auth error reason
  private getAuthErrorReason(error: AxiosError): string {
    const data = error.response?.data as any;
    if (data?.code === 'token_expired') return 'Token expired';
    if (data?.code === 'csrf_failed') return 'CSRF check failed';
    if (data?.detail?.toLowerCase().includes('expired')) return 'Token expired';
    if (data?.detail?.toLowerCase().includes('invalid')) return 'Invalid token';
    return data?.detail || data?.message || 'Unknown authentication error';
  }

  // Helper to determine specific authorization error reason
  private getAuthorizationErrorReason(error: AxiosError): string {
    const data = error.response?.data as any;
    
    if (!localStorage.getItem('selectedClientId')) {
      return 'No client ID selected';
    }
    
    if (data?.message?.toLowerCase().includes('client')) {
      return 'Not authorized for this client';
    }
    
    if (data?.message?.toLowerCase().includes('permission')) {
      return 'Missing required permissions';
    }
    
    if (data?.message?.toLowerCase().includes('role')) {
      return 'Missing required role';
    }
    
    return data?.message || 'Unknown authorization error';
  }

  private isRetryableError(error: Error): boolean {
    // Network errors
    if (error.message.includes('Network Error')) return true;
    
    // Rate limiting (429)
    if (error instanceof ApiServiceError && error.status === 429) return true;
    
    // Don't retry auth errors (401) - handle them immediately
    if (error instanceof ApiServiceError && error.status === 401) return false;
    
    return false;
  }

  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    retryCount: number = 0
  ): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error) {
      const shouldRetry = this.isRetryableError(error as Error) && retryCount < this.MAX_RETRIES;
      
      if (shouldRetry) {
        // Exponential backoff with jitter
        const baseDelay = this.RETRY_DELAY;
        const exponentialDelay = baseDelay * Math.pow(2, retryCount);
        const jitter = Math.random() * 200; // Add up to 200ms of random jitter
        const delay = exponentialDelay + jitter;
        
        console.log(`[ApiService] Retrying request (attempt ${retryCount + 1}/${this.MAX_RETRIES}) after ${Math.round(delay)}ms`);
        
        // Notify any registered handlers about the retry
        this.notifyRetry({
          attempt: retryCount + 1,
          maxAttempts: this.MAX_RETRIES,
          delay: Math.round(delay),
          error: error as Error
        });
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.retryRequest(requestFn, retryCount + 1);
      }
      
      throw error;
    }
  }

  // Register a handler for retry notifications
  public registerRetryHandler(handler: RetryHandler): () => void {
    this.retryHandlers.push(handler);
    return () => {
      this.retryHandlers = this.retryHandlers.filter(h => h !== handler);
    };
  }
  
  private notifyRetry(info: RetryInfo): void {
    this.retryHandlers.forEach(handler => {
      try {
        handler(info);
      } catch (error) {
        console.error('Error in retry handler:', error);
      }
    });
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any,
    config: AxiosRequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.retryRequest<T>(() => this.api.request({
      method,
      url: endpoint,
      data,
      ...config
    }));
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, undefined, config);
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, config);
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, config?.data, config);
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', url, data, config);
  }

  // Helper method to test connection and CORS
  async testConnection(): Promise<{ success: boolean; message: string; timestamp?: string }> {
    try {
      // Try the test-connection endpoint which should be public
      const response = await this.api.get('/api/test-connection');
      return {
        success: true,
        message: 'Connection successful',
        timestamp: response.data?.timestamp
      };
    } catch (error) {
      console.error('Test connection failed, trying health endpoint:', error);
      
      try {
        // Fall back to health endpoint
        const healthResponse = await this.api.get('/api/health');
        return {
          success: true,
          message: 'Connection successful through health endpoint',
          timestamp: healthResponse.data?.timestamp
        };
      } catch (healthError) {
        console.error('Health endpoint also failed:', healthError);
        
        let message = 'Unknown error occurred';
        
        if (error instanceof Error) {
          if (error.message.includes('Network Error') || error.message.includes('CORS')) {
            message = 'CORS error: The backend server may not have proper CORS headers configured';
          } else if (error.message.includes('404')) {
            message = 'Server reached but test endpoint not found. API may be working but endpoints are missing.';
          } else if (error.message.includes('401') || error.message.includes('403')) {
            message = 'Authentication/Authorization issue. API is reachable but requires authentication.';
          } else {
            message = `Error: ${error.message}`;
          }
        }
        
        return {
          success: false,
          message
        };
      }
    }
  }

  // Helper method to cancel requests
  cancelRequest(requestId: string): void {
    // Implementation for request cancellation if needed
  }

  // Bounces
  async getBounces({ clientId, startDate, endDate, skip = 0, limit = 50, email_address, bounce_type, bounce_sub_type, common_subject, sortBy, sortOrder }: GetBouncesParams) {
    const params: any = { client_id: clientId, skip, limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (email_address) params.email_address = email_address;
    if (bounce_type) params.bounce_type = bounce_type;
    if (bounce_sub_type) params.bounce_sub_type = bounce_sub_type;
    if (common_subject) params.common_subject = common_subject;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    return this.get('/api/contacts/bounces', { params });
  }
  async getBouncesByDay({ clientId, days = 30 }: GetBouncesByDayParams) {
    return this.get('/api/contacts/bounces/by-day', { params: { client_id: clientId, days } });
  }
  async getBounceDetail(id: number) {
    return this.get(`/api/contacts/bounces/${id}`);
  }

  // Complaints
  async getComplaints({ clientId, startDate, endDate, skip = 0, limit = 50, email_address, complaint_feedback_type, complaint_sub_type, common_subject, sortBy, sortOrder }: GetComplaintsParams) {
    const params: any = { client_id: clientId, skip, limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (email_address) params.email_address = email_address;
    if (complaint_feedback_type) params.complaint_feedback_type = complaint_feedback_type;
    if (complaint_sub_type) params.complaint_sub_type = complaint_sub_type;
    if (common_subject) params.common_subject = common_subject;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    return this.get('/api/contacts/complaints', { params });
  }
  async getComplaintsByDay({ clientId, days = 30 }: GetComplaintsByDayParams) {
    return this.get('/api/contacts/complaints/by-day', { params: { client_id: clientId, days } });
  }
  async getComplaintDetail(id: number) {
    return this.get(`/api/contacts/complaints/${id}`);
  }

  // Unsubscribes
  async getUnsubscribes({ clientId, startDate, endDate, skip = 0, limit = 50, email_address, first_name, last_name, dnc_email_note, sortBy, sortOrder }: GetUnsubscribesParams) {
    const params: any = { client_id: clientId, skip, limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (email_address) params.email_address = email_address;
    if (first_name) params.first_name = first_name;
    if (last_name) params.last_name = last_name;
    if (dnc_email_note) params.dnc_email_note = dnc_email_note;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    return this.get('/api/contacts/unsubscribes', { params });
  }
  async getUnsubscribesByDay({ clientId, days = 30 }: GetUnsubscribesByDayParams) {
    return this.get('/api/contacts/unsubscribes/by-day', { params: { client_id: clientId, days } });
  }

  async getClient(clientId: number) {
    return this.get(`/api/clients/${clientId}`);
  }
  async getUnsubscribeDetail(id: number) {
    return this.get(`/api/contacts/unsubscribes/${id}`);
  }
}

// Export the singleton instance
export const apiService = ApiService.getInstance();
export default apiService;

// Hook to get API service instance
export const useApiService = () => {
  return apiService;
}; 